/* eslint-disable no-unused-expressions */
import {
  FieldRules,
  GroupOfRules
} from '@ingka-group-digital/customer-fields-validation-rules';
import CONFIG from 'Config/settings';
import cache from 'memory-cache';
import { ReduxStore } from 'Redux';
import { getFieldMessages, getFormValues } from 'Redux/forms/formsHelper';
import {
  convertUnifiedDateStringToDateObject,
  getAge,
  getSpecificDateStringInUnifiedFormat
} from 'Utils/date/dateUtils';
import { exists, t } from 'Utils/localization/i18next';
import FieldKeyConstants from './fieldKeyConstants';
import {
  PasswordErrorKeys,
  validateIdenticalChars,
  validateLowerCase,
  validateMaxLength,
  validateMinLength,
  validateNumbers,
  validateSpecialCharacters,
  validateUpperCase
} from './password';

const cacheTime = 1 * 24 * 60 * 60 * 1000; // 1 day in milliseconds
const memCache = new cache.Cache();

const getValidationRulesByRegex = (ruleName, language) => {
  const cacheKey = `${ruleName}_${language}`;
  let validationRules = memCache.get(cacheKey);

  if (!validationRules) {
    validationRules = GroupOfRules.extractValidationRulesByRegex(
      ruleName,
      language
    );
    memCache.put(cacheKey, validationRules, cacheTime);
  }

  return validationRules;
};

const generalRegex = getValidationRulesByRegex('GENERALREGEX', 'JavaScript');
const zipCodeRegex = getValidationRulesByRegex('ZIPCODEREGEX', 'JavaScript');
const ssnRegex = getValidationRulesByRegex('SSNREGEX', 'JavaScript');

const getFieldKeyByLabel = value => {
  return Object.keys(FieldKeyConstants).find(
    key => FieldKeyConstants[key] === value
  );
};

const removeFormsPrefix = label => {
  const prefix = 'forms.';
  if (label.startsWith(prefix)) {
    return label.slice(prefix.length);
  }
  return label;
};

const getValidationRulesByFieldLabel = (
  label,
  countryCode = CONFIG.COUNTRY.toUpperCase()
) => {
  const fieldKey = getFieldKeyByLabel(removeFormsPrefix(label));
  const validationRules = FieldRules.getValidationRulesByField(
    [fieldKey],
    countryCode,
    'JavaScript'
  );
  return validationRules;
};

const validationMessage = (validatorName, label, message, args) => {
  if (message) {
    return [
      t(message, {
        field:
          CONFIG.FEATURE.VALIDATION &&
          CONFIG.FEATURE.VALIDATION.KEEP_PLACEHOLDER_CAPITALIZATION
            ? t(label)
            : t(label).toLocaleLowerCase(),
        ...args
      })
    ];
  }
  if (label) {
    return [
      t(`forms.validation.${validatorName}`, {
        field:
          CONFIG.FEATURE.VALIDATION &&
          CONFIG.FEATURE.VALIDATION.KEEP_PLACEHOLDER_CAPITALIZATION
            ? t(label)
            : t(label).toLocaleLowerCase(),
        ...args
      })
    ];
  }
  return [t('forms.validation.invalidGeneral')];
};

const addressLineWithApartmentNumber = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } = generalRegex?.elem?.addressLineWithApartmentNumber ?? {};
  const { flag } = generalRegex?.elem?.addressLineWithApartmentNumber ?? {};
  const addressLineWithApartmentNumberRegExp = new RegExp(
    regexp || '',
    flag || ''
  );
  if (!addressLineWithApartmentNumberRegExp.test(value)) {
    return validationMessage('addressLineWithApartmentNumber', label, message);
  }
  return undefined;
};

const birthDate = (
  value = '',
  label,
  {
    minAge = Number.MIN_SAFE_INTEGER,
    maxAge = Number.MAX_SAFE_INTEGER,
    format = 'DD-MM-YYYY',
    message
  }
) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } = generalRegex?.elem?.dateFormat?.[format] ?? {};
  const { flag } = generalRegex?.elem?.dateFormat?.[format] ?? {};
  const birthDateRegExp = new RegExp(regexp || '', flag || '');
  if (!birthDateRegExp.test(value)) {
    return validationMessage('birthDateFormat', label, message, {
      format
    });
  }

  const formattedDateStr = getSpecificDateStringInUnifiedFormat(value, format);
  const unifiedDateRegexp = generalRegex?.elem?.date?.regexp ?? '';
  const unifiedDateRegexFlag = generalRegex?.elem?.date?.flag ?? '';
  const dateUnifiedFormat = new RegExp(unifiedDateRegexp, unifiedDateRegexFlag);
  if (!dateUnifiedFormat.test(formattedDateStr)) {
    return validationMessage(
      exists('forms.validation.birthDateInvalid')
        ? 'birthDateInvalid'
        : 'invalid',
      label,
      message,
      {
        format
      }
    );
  }

  const birthDateObj = convertUnifiedDateStringToDateObject(formattedDateStr);
  const age = getAge(birthDateObj);

  if (age < minAge || age > maxAge) {
    const minStr = minAge !== Number.MIN_SAFE_INTEGER ? minAge : '';
    const maxStr = maxAge !== Number.MAX_SAFE_INTEGER ? maxAge : '';
    const delimiterStr = minStr !== '' && maxStr !== '' ? '-' : '';
    const range = `${minStr}${delimiterStr}${maxStr}`;
    return validationMessage('birthDateAge', label, message, { range });
  }
  return undefined;
};

const alphanumericDashAndSpace = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } = generalRegex?.elem?.alphanumericDashAndSpace ?? {};
  const { flag } = generalRegex?.elem?.alphanumericDashAndSpace ?? {};
  const alphanumericDashAndSpaceRegExp = new RegExp(regexp || '', flag || '');
  if (!alphanumericDashAndSpaceRegExp.test(value)) {
    return validationMessage('alphanumericDashAndSpace', label, message);
  }
  return undefined;
};

const email = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } = generalRegex?.elem?.email ?? {};
  const { flag } = generalRegex?.elem?.email ?? {};
  const emailRegExp = new RegExp(regexp || '', flag || '');
  if (!emailRegExp.test(value)) {
    return validationMessage('email', label, message);
  }
  return undefined;
};

const lengthRange = (value = '', label, { min, max, message }) => {
  if (!value.length) {
    return undefined;
  }
  const maxValue = !max ? min : max;

  if (value.length < parseInt(min) || value.length > parseInt(maxValue)) {
    const range = min === maxValue ? min : `${min}-${maxValue}`;
    return validationMessage('lengthRange', label, message, { range });
  }
  return undefined;
};

const mandatory = (value = false, label, { message }) => {
  const newValue = typeof value === 'string' ? value.trim() : value;
  if (!newValue) {
    return validationMessage('mandatory', label, message);
  }
  return undefined;
};

const mandatoryIfOtherFieldMatchesRegex = (
  value = '',
  label,
  { fieldLabel, otherFieldName, otherFieldLabel, message, regex },
  formId,
  setFormFieldMessages
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(ReduxStore.getState(), formId);
  const otherFieldValue = formValues[otherFieldName];
  const isOtherFieldMatchesRegex = new RegExp(regex).test(
    otherFieldValue || ''
  );
  if (!value && isOtherFieldMatchesRegex) {
    return validationMessage('mandatory', label, message, {
      otherField: t(otherFieldLabel).toLocaleLowerCase(),
      field: t(fieldLabel || label).toLocaleLowerCase()
    });
  }

  // If the field currently being validated is valid, the depending field might
  // also be valid. Remove the validation message originating from this validator
  // from the depending field's list of validation messages.
  if (!setFormFieldMessages) {
    return undefined;
  }
  const currentMessages = getFieldMessages(reduxState, formId, otherFieldName);

  const originalMessage = validationMessage(
    'mandatory',
    otherFieldLabel,
    message,
    {
      otherField: t(label).toLocaleLowerCase()
    }
  )[0];

  const filteredMessages = currentMessages.filter(m => m !== originalMessage);
  setFormFieldMessages(formId, otherFieldName, filteredMessages);
  return undefined;
};

const mandatoryIfProfileFieldChanged = (
  value = false,
  label,
  { field, message },
  formId
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);
  const profileFieldValue =
    typeof reduxState.profile[field] === 'string'
      ? reduxState.profile[field].toLocaleLowerCase()
      : reduxState.profile[field];
  const formValue =
    typeof formValues[field] === 'string'
      ? formValues[field].toLocaleLowerCase()
      : formValues[field];
  const fieldChanged = profileFieldValue !== formValue;
  const newValue =
    typeof value === 'string' ? value.trim().toLowerCase() : value;
  if (fieldChanged && !newValue) {
    return validationMessage('mandatory', label, message);
  }
  return undefined;
};

const matchWithFormField = (value = '', label, { field, message }, formId) => {
  if (!value.length) {
    return undefined;
  }
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);

  if (formValues[field] && value !== formValues[field]) {
    return validationMessage('matchWithFormField', label, message);
  }
  return undefined;
};

const notInjectionChars = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } = generalRegex?.elem?.notInjectionChars ?? {};
  const { flag } = generalRegex?.elem?.notInjectionChars ?? {};
  const notInjectionCharsRegExp = new RegExp(regexp || '', flag || '');
  if (!notInjectionCharsRegExp.test(value)) {
    return validationMessage('notInjectionChars', label, message);
  }
  return undefined;
};

const notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars = (
  value = '',
  label,
  { message }
) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } =
    generalRegex?.elem
      ?.notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars ?? {};
  const { flag } =
    generalRegex?.elem
      ?.notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars ?? {};
  const notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionCharsRegExp = new RegExp(
    regexp || '',
    flag || ''
  );
  if (
    !notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionCharsRegExp.test(
      value
    )
  ) {
    return validationMessage(
      'notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars',
      label,
      message
    );
  }
  return undefined;
};

const notOnlySpaceNumberSpecialCharsAndNotInjectionChars = (
  value = '',
  label,
  { message }
) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } =
    generalRegex?.elem?.notOnlySpaceNumberSpecialCharsAndNotInjectionChars ??
    {};
  const { flag } =
    generalRegex?.elem?.notOnlySpaceNumberSpecialCharsAndNotInjectionChars ??
    {};
  const notOnlySpaceNumberSpecialCharsAndNotInjectionCharsRegExp = new RegExp(
    regexp || '',
    flag || ''
  );
  if (!notOnlySpaceNumberSpecialCharsAndNotInjectionCharsRegExp.test(value)) {
    return validationMessage(
      'notOnlySpaceNumberSpecialCharsAndNotInjectionChars',
      label,
      message
    );
  }
  return undefined;
};

const notOnlySpaceSpecialCharsAndNotInjectionChars = (
  value = '',
  label,
  { message }
) => {
  if (!value.length) {
    return undefined;
  }
  const { regexp } =
    generalRegex?.elem?.notOnlySpaceSpecialCharsAndNotInjectionChars ?? {};
  const { flag } =
    generalRegex?.elem?.notOnlySpaceSpecialCharsAndNotInjectionChars ?? {};
  const notOnlySpaceSpecialCharsAndNotInjectionCharsRegExp = new RegExp(
    regexp || '',
    flag || ''
  );
  if (!notOnlySpaceSpecialCharsAndNotInjectionCharsRegExp.test(value)) {
    return validationMessage(
      'notOnlySpaceSpecialCharsAndNotInjectionChars',
      label,
      message
    );
  }
  return undefined;
};

const password = (value = '') => {
  const messages = [];
  const isNumbersOrSpecialCharacters =
    validateNumbers.check(value) || validateSpecialCharacters.check(value);
  !validateMinLength.check(value) && messages.push(PasswordErrorKeys[0]);
  !validateMaxLength(value) && messages.push(PasswordErrorKeys[0]);
  !validateIdenticalChars.check(value) && messages.push(PasswordErrorKeys[1]);
  !validateLowerCase.check(value) && messages.push(PasswordErrorKeys[2]);
  !validateUpperCase.check(value) && messages.push(PasswordErrorKeys[3]);
  !isNumbersOrSpecialCharacters && messages.push(PasswordErrorKeys[4]);
  return messages;
};

const groupRegex = (
  value = '',
  label,
  { fieldLabel, otherFieldName, otherFieldLabel, message, regex },
  formId,
  setFormFieldMessages
) => {
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(ReduxStore.getState(), formId);
  const otherFieldValue = formValues[otherFieldName];
  const isGroupMatchesRegex = new RegExp(regex, 'i').test(
    `${value}${otherFieldValue || ''}`
  );
  if (isGroupMatchesRegex) {
    return validationMessage('invalid', label, message, {
      otherField: t(otherFieldLabel).toLocaleLowerCase(),
      field: t(fieldLabel || label).toLocaleLowerCase()
    });
  }

  // If the field currently being validated is valid, the depending field might
  // also be valid. Remove the validation message originating from this validator
  // from the depending field's list of validation messages.
  if (!setFormFieldMessages) {
    return undefined;
  }
  const currentMessages = getFieldMessages(reduxState, formId, otherFieldName);

  const originalMessage = validationMessage(
    'invalid',
    otherFieldLabel,
    message,
    {
      otherField: t(label).toLocaleLowerCase()
    }
  )[0];

  const filteredMessages = currentMessages.filter(m => m !== originalMessage);
  setFormFieldMessages(formId, otherFieldName, filteredMessages);
  return undefined;
};

const regex = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const validationRules = getValidationRulesByFieldLabel(label);

  // try general regex first
  const regexpString = validationRules[0]?.regex?.regexp || '';
  const regexp = new RegExp(regexpString);
  if (!regexp.test(value)) {
    return validationMessage('regex', label, message);
  }

  // then try special regex if defined
  const regexpSpecialString = validationRules[0]?.special_regex?.regexp || '';
  const regexpSpecial = new RegExp(regexpSpecialString);
  if (!regexpSpecial.test(value)) {
    return validationMessage('regex', label, message);
  }

  return undefined;
};

// Luhn's algorithm
const isSSNValid = ssn => {
  const sum = [...ssn]
    .reverse()
    .slice(0, 10)
    .map(Number)
    .reduce((previousSum, currentNumber, i) => {
      let digit = currentNumber;
      if (i % 2) {
        digit *= 2;
      }
      if (digit > 9) {
        digit -= 9;
      }
      return previousSum + digit;
    });
  return sum % 10 === 0;
};

const socialSecurityNumber = (value = '', label, { message }) => {
  if (!value.length) {
    return undefined;
  }
  const country = CONFIG.COUNTRY.toUpperCase();
  const { regexp } = ssnRegex?.elem?.[country] ?? {};
  const { flag } = ssnRegex?.elem?.[country] ?? {};
  const socialSecurityNumberRegExp = new RegExp(regexp || '', flag || '');
  if (!socialSecurityNumberRegExp.test(value)) {
    return validationMessage('socialSecurityNumber', label, message);
  }
  if (!isSSNValid(value)) {
    return validationMessage('socialSecurityNumber', label, message);
  }
  return undefined;
};

const zipCode = (value = '', label, args, formId) => {
  if (!value.length) {
    return undefined;
  }

  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);
  const country = formValues.addressCountryCode || CONFIG.COUNTRY.toUpperCase();

  const regexp =
    zipCodeRegex?.elem?.[country]?.regexp ??
    zipCodeRegex?.elem?.default?.regexp ??
    '';
  const flag =
    zipCodeRegex?.elem?.[country]?.flag ??
    zipCodeRegex?.elem?.default?.flag ??
    '';

  const zipCodeRegexByCountry = new RegExp(regexp, flag);

  if (!zipCodeRegexByCountry.test(value)) {
    return [
      country === CONFIG.COUNTRY.toUpperCase()
        ? t('forms.validation.zipCode')
        : t('forms.validation.zipCodeInternational', { country })
    ];
  }
  return undefined;
};

const phone = (value = '', label, { phoneType = 'mobile' }, formId) => {
  if (!value.length) {
    return undefined;
  }
  const reduxState = ReduxStore.getState();
  const formValues = getFormValues(reduxState, formId);
  const country =
    formValues[`${phoneType}Country`] || CONFIG.COUNTRY.toUpperCase();

  const validationRules = getValidationRulesByFieldLabel(label, country);
  const regexpByLabel = validationRules[0]?.regex?.regexp || '';
  const flagByLabel = validationRules[0]?.regex?.flag || '';
  const regexp = new RegExp(regexpByLabel, flagByLabel);

  if (!regexp.test(value)) {
    return [
      country === CONFIG.COUNTRY.toUpperCase()
        ? t(`forms.validation.${phoneType}`)
        : t(`forms.validation.${phoneType}International`)
    ];
  }
  return undefined;
};

const childBirthDate = (
  value = '',
  label,
  { required, minAge = 0, maxAge = 18, format, childIndex },
  formId
) => {
  const { noOfChildren = 0 } = getFormValues(ReduxStore.getState(), formId);

  if (noOfChildren < childIndex + 1) {
    // Field should not be visible/validated
    // if "childIndex" exceeds "noOfChildren".
    return undefined;
  }
  if (required) {
    const msg = mandatory(value, label, {
      message: 'forms.validation.mandatoryGeneral'
    });
    if (msg) {
      return msg;
    }
  }
  if (format) {
    const msg = birthDate(value, label, {
      minAge,
      maxAge,
      format,
      message: 'forms.validation.childBirthdate'
    });
    if (msg) {
      return msg;
    }
  }
  return undefined;
};

// Validates Portugal NIF Regex and Module 11 algorithm
const nif = (value = '') => {
  if (!value.length) {
    return undefined;
  }
  const error = [t('forms.validation.nif')];
  const nifRegex = /([123568]\d{1}|45|7[01245789]|9[0189])(\d{7})/;
  if (!nifRegex.test(value)) {
    return error;
  }
  const mod11 =
    Number(
      [9, 8, 7, 6, 5, 4, 3, 2].reduce(
        (total, multiplier, index) => total + value[index] * multiplier,
        0
      )
    ) % 11;
  const checkDigit = mod11 < 2 ? 0 : 11 - mod11;
  if (checkDigit !== Number(value[8])) {
    return error;
  }
  return undefined;
};

// Validates ES NIF,DNI,NIE Regex.
const dni = (value = '') => {
  if (!value.length) {
    return undefined;
  }
  const error = [t('forms.validation.document')];
  const dniRegex = /^((\d{8})|([klm|KLM]\d{7}))[a-zA-Z]{1}$/;
  const nieRegex = /^([xyz|XYZ]\d{7}[a-zA-Z]{1}$)/;
  if (!dniRegex.test(value) && !nieRegex.test(value)) {
    return error;
  }
  return undefined;
};

const passport = (value = '') => {
  if (!value.length) {
    return undefined;
  }
  const error = [t('forms.validation.passport')];
  const passportRegex = /^([a-z]|[A-Z]|[0-9]){1,20}$/;
  if (!passportRegex.test(value)) {
    return error;
  }
  return undefined;
};

export default {
  alphanumericDashAndSpace,
  addressLineWithApartmentNumber,
  birthDate,
  email,
  groupRegex,
  lengthRange,
  mandatory,
  mandatoryIfOtherFieldMatchesRegex,
  mandatoryIfProfileFieldChanged,
  matchWithFormField,
  notInjectionChars,
  notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceSpecialCharsAndNotInjectionChars,
  password,
  regex,
  socialSecurityNumber,
  zipCode,
  phone,
  childBirthDate,
  nif,
  dni,
  passport
};
